import React, { useMemo } from "react";
import { Modal, Drawer, Tag, Space } from "antd";
import { CheckCircleOutlined, ClockCircleOutlined } from "@ant-design/icons";
import styled from "styled-components";
import DOMPurify from "dompurify";
import Logo from "../../assets/logo.svg";

const DocWrapper = styled.div`
  font-family: 'Georgia', serif;
  background: #fff;
  padding: 32px 40px;
  max-width: 800px;
  margin: 0 auto;
  color: #1a1a1a;
  line-height: 1.7;

  .doc-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 32px;
    padding-bottom: 16px;
    border-bottom: 2px solid #014c75;
  }

  .doc-logo {
    height: 36px;
  }

  .doc-watermark {
    font-size: 11px;
    color: #999;
    text-align: right;
    font-family: sans-serif;
  }

  .doc-title {
    font-size: 20px;
    font-weight: 700;
    color: #014c75;
    text-align: center;
    margin-bottom: 24px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  .doc-body {
    font-size: 14px;
    color: #222;
    line-height: 1.85;

    p {
      margin-bottom: 1em;
    }

    strong, b {
      font-weight: 700;
      color: #014c75;
    }

    h3, h4 {
      font-size: 15px;
      font-weight: 700;
      color: #014c75;
      margin: 1.2em 0 0.5em;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .signature-block {
      margin-top: 32px;
      padding-top: 16px;
      border-top: 1px solid #d0d0d0;
      font-size: 13px;
    }
  }

  .doc-footer {
    margin-top: 32px;
    padding-top: 16px;
    border-top: 1px solid #e0e0e0;
    font-size: 11px;
    color: #999;
    text-align: center;
    font-family: sans-serif;
  }
`;

const PendingPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  color: #999;

  .pending-icon {
    font-size: 48px;
    color: #faad14;
    margin-bottom: 16px;
  }

  h3 {
    color: #555;
    margin-bottom: 8px;
  }

  p {
    font-size: 13px;
  }
`;

const DOC_TYPE_LABELS = {
  nda: 'Non-Disclosure Agreement',
  contract: 'Contract Agreement',
  declaration: 'Talent Declaration',
};

const ALLOWED_TAGS = ['p', 'br', 'strong', 'b', 'em', 'i', 'h1', 'h2', 'h3', 'h4', 'ul', 'li', 'ol', 'span', 'div', 'blockquote'];
const ALLOWED_ATTR = ['class', 'style'];

/**
 * Sanitize and normalise document content for display.
 * - If content already contains HTML tags (e.g. from rich-text editor), just sanitize.
 * - Otherwise convert legacy plain-text (with \n line-breaks) to paragraph HTML.
 */
const plainTextToHtml = (text) => {
  if (!text) return '';

  // Detect HTML content from the rich-text editor (react-quill stores <p>, <strong> etc.)
  if (/<[a-z][\s\S]*>/i.test(text)) {
    return DOMPurify.sanitize(text, { ALLOWED_TAGS, ALLOWED_ATTR });
  }

  // Legacy plain-text conversion
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  const html = escaped
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .split(/\n\n+/)
    .map((para) => {
      const trimmed = para.trim();
      if (!trimmed) return '';
      const isHeading = /^[A-Z&;,\s]{3,60}$/.test(trimmed.replace(/&amp;/g, '&'));
      if (isHeading) return `<h4>${trimmed}</h4>`;
      return `<p>${trimmed.replace(/\n/g, '<br />')}</p>`;
    })
    .filter(Boolean)
    .join('\n');

  return DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR });
};

export const ComplianceDocViewer = ({ document, open, onClose, mode = 'drawer' }) => {
  if (!document) return null;

  // Show the placeholder when there is no rendered body to display.
  // The backend deliberately omits renderedContent for customers viewing pending docs
  // (403 path) or returns it fully for partners/admin so they can review before signing.
  const hasContent = Boolean(document.renderedContent && document.renderedContent.trim());
  const isPending = !hasContent;
  const title = document.templateTitle || DOC_TYPE_LABELS[document.docType] || 'Compliance Document';

  const sanitizedHtml = useMemo(
    () => plainTextToHtml(document.renderedContent),
    [document.renderedContent]
  );

  const docContent = (
    <DocWrapper>
      <div className="doc-header">
        <img src={Logo} alt="HireNXT" className="doc-logo" />
        <div className="doc-watermark">
          <div>HireNXT Technologies Pvt. Ltd.</div>
          {hasContent && document.signedAt && (
            <div>
              Digitally acknowledged on{' '}
              {new Date(document.signedAt).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
              })}
            </div>
          )}
        </div>
      </div>

      <div className="doc-title">{title}</div>

      {isPending ? (
        <PendingPlaceholder>
          <ClockCircleOutlined className="pending-icon" />
          <h3>Awaiting Partner Sign-off</h3>
          <p>This document has been generated and is pending digital acknowledgement by the Partner.</p>
        </PendingPlaceholder>
      ) : (
        <>
          <div
            className="doc-body"
            // sanitized by DOMPurify before being set
            dangerouslySetInnerHTML={{ __html: sanitizedHtml || 'Document content not available.' }}
          />
          <div className="doc-footer">
            This document was digitally acknowledged via HireNXT platform. Powered by HireNXT Technologies Pvt. Ltd.
          </div>
        </>
      )}
    </DocWrapper>
  );

  // Status tag reflects the actual document.status from the server, not the
  // content-presence flag, so the title bar always shows the true state.
  const statusTag = document.status === 'signed' ? (
    <Tag icon={<CheckCircleOutlined />} color="success">Signed</Tag>
  ) : (
    <Tag icon={<ClockCircleOutlined />} color="warning">Pending Sign-off</Tag>
  );

  // Inline mode: render document body directly with no Modal/Drawer wrapper,
  // suitable for embedding inside an existing Drawer or Modal
  if (mode === 'inline') {
    return docContent;
  }

  if (mode === 'modal') {
    return (
      <Modal
        open={open}
        onCancel={onClose}
        title={
          <Space>
            <span>{title}</span>
            {statusTag}
          </Space>
        }
        width={860}
        footer={null}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto', padding: 0 }}
      >
        {docContent}
      </Modal>
    );
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={
        <Space>
          <span>{title}</span>
          {statusTag}
        </Space>
      }
      width={720}
      bodyStyle={{ padding: 0 }}
    >
      {docContent}
    </Drawer>
  );
};

export default ComplianceDocViewer;
